import React from 'react';
import { Box } from '@mui/material';
import { useConfirm, useLocales, useStatsig } from '../../../../hooks';
import { makeStyles } from 'tss-react/mui';
import { FormProvider, useForm } from 'react-hook-form';
import { ValidatorForm } from 'react-material-ui-form-validator';
import Button from '../../Button';
import {
  ContentCollectionType,
  ContentGroupRegionsBody,
  ContentGroupsRegionsBody,
  ContentGroupsType,
  ContentPatchBody,
  MediaResponse,
  RedirectionType as EnrichRedirectionType,
  ReverseOrder
} from '../../../../API';
import { useData } from '../../../../data-layer';
import { usePermissionsGuard } from '../../../../hooks/General/usePermissionsGuard';
import { HomepageOptions } from '../../../../state/theme';
import { useRecoilValue } from 'recoil';
import { enrichTestIds } from '../../TestsIds';
import { ReverseChronological } from './ReverseChronological';
import { ContentEntitlementFiltering as ContentEntitlementFilteringField } from './ContentEntitlementFiltering';
import { ContentLabels } from './ContentLabels';
import { DeepLink } from './DeepLink';
import { SearchSynonyms } from './SearchSynonyms';
import {
  ContentLabelBody,
  ContentPatchBodyWithContentLabels,
  MediaResponseWithContentLabels
} from './ContentLabelTypes';
import { isEqual } from 'lodash-es';
import { StatsigGates } from '../../../../utils/consts/statsigGates';

interface EnrichFormProps {
  contentId?: string;
}

// Reverse Chronological orders seasons and episodes, so only episodic content can use it. The API rejects
// the field with a 422 for every other type, so this has to be an allow list: a type missing from it is a
// video as far as the API is concerned, even if the CMS does not know about it yet.
const contentTypesWithReverseChronological: string[] = [
  ContentCollectionType.SERIES,
  ContentCollectionType.NOVELA,
  ContentCollectionType.SHORT_NOVELA
];

// Search synonyms are authored per VOD asset. This mirrors the allow list the API guards the patch
// with, so an editor is never shown a field the save would reject
const contentTypesWithSynonyms: string[] = [
  ContentCollectionType.MOVIE,
  ContentCollectionType.SERIES,
  ContentCollectionType.NOVELA,
  ContentCollectionType.SHORT_NOVELA,
  ContentCollectionType.CLIP
];

export interface ContentEntitlementFiltering {
  contentGroup: ContentGroupsType[];
  countryCodes: string[];
}

export interface EnrichForm {
  reverseChronologicalOrder: string;
  contentEntitlementFiltering: ContentEntitlementFiltering[];
  contentLabels: ContentLabelBody[];
  redirection: { type: EnrichRedirectionType; value: string };
  synonyms: string[];
}

function getEnrichDefaultValues(content?: MediaResponse): EnrichForm {
  const defaultValues = {} as EnrichForm;
  const contentType = content?.contentType ?? '';

  if (contentTypesWithReverseChronological.includes(contentType)) {
    defaultValues.reverseChronologicalOrder = content?.reverseOrder || ReverseOrder.NONE;
  }

  if (contentTypesWithSynonyms.includes(contentType)) {
    defaultValues.synonyms = content?.synonyms ?? [];
  }

  defaultValues.contentEntitlementFiltering =
    content?.contentGroupsRegions?.map(({ contentGroup, countryCodes }) => ({
      contentGroup,
      countryCodes
    })) ?? [];

  const redirection = content?.extendedMetadata?.redirection;
  defaultValues.redirection = {
    type: redirection?.type ?? ('' as EnrichRedirectionType),
    value: redirection?.value ?? ''
  };

  const storedContentLabels = (content as MediaResponseWithContentLabels)?.contentLabels?.default ?? [];
  defaultValues.contentLabels = storedContentLabels.map(({ label, endDate, displayText }) => ({
    label,
    endDate: endDate ?? '',
    displayText: displayText ?? ''
  }));

  return defaultValues;
}

export const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(4)
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4)
  },
  saveAndPublishBtn: {
    marginTop: theme.spacing(4)
  }
}));

export function EnrichForm({ contentId }: EnrichFormProps): JSX.Element {
  const { classes } = useStyles();
  const {
    media: {
      state: { withRecordById, withSearchParams },
      hook: { closeForm, patchContent, clearPersistedSearchParams }
    }
  } = useData();
  const { t } = useLocales();
  const { confirm } = useConfirm();
  const { CheckGate } = useStatsig();
  const isContentLabelsActive = CheckGate(StatsigGates.contentLabelsActive);
  const { canPublish: canPublishReverseChron } = usePermissionsGuard({
    homepageOption: HomepageOptions.METADATA
  });
  const content = useRecoilValue(withRecordById(contentId));
  const lastSearch = useRecoilValue(withSearchParams);

  const hasReverseChronological = contentTypesWithReverseChronological.includes(content?.contentType ?? '');
  const hasSynonyms = contentTypesWithSynonyms.includes(content?.contentType ?? '');

  const storedSynonyms = content?.synonyms ?? [];
  const storedContentLabels = (content as MediaResponseWithContentLabels)?.contentLabels?.default ?? [];

  const methods = useForm<EnrichForm>({
    defaultValues: getEnrichDefaultValues(content)
  });
  const { control } = methods;

  const [isSaving, setIsSaving] = React.useState(false);

  const handleSubmit = async (data: EnrichForm) => {
    if (contentId) {
      const isConfirmed = await confirm({
        confirmText: t('general.update'),
        confirmColor: 'warning',
        body: t('confirm.enrich_update'),
        'data-testid': enrichTestIds.updateConfirm
      });

      if (!isConfirmed) return;

      const reverseOrder = data.reverseChronologicalOrder as ReverseOrder;
      let contentGroups: ContentGroupRegionsBody | undefined = undefined;

      if (data.contentEntitlementFiltering.length > 0) {
        const regions: ContentGroupsRegionsBody[] = [];
        data.contentEntitlementFiltering.forEach(({ contentGroup, countryCodes }) => {
          regions.push({
            contentGroup,
            countryCodes
          });
        });
        contentGroups = { regions };
      }

      // Content search does not index synonyms yet, so reopening an asset in a new session shows an
      // empty box even when the asset has them stored. Sending the field only once the editor has
      // actually changed it keeps an unrelated save from wiping those stored values.
      const synonymsChanged = hasSynonyms && !isEqual(data.synonyms ?? [], storedSynonyms);

      // The section is behind a Statsig gate. While it is off the editor cannot change labels,
      // so the patch leaves contentLabels out and whatever is stored stays as it is.
      let contentLabelsPatch: { default: ContentLabelBody[] } | null | undefined;
      if (isContentLabelsActive) {
        // endDate and displayText are optional for CAPI, so empty fields are left out of the label
        const contentLabels: ContentLabelBody[] = data.contentLabels.map(({ label, endDate, displayText }) => ({
          label,
          ...(endDate && { endDate }),
          ...(displayText && { displayText })
        }));
        // An empty list is a 422. Clearing labels that were stored sends null; an asset that never
        // had labels omits the field so the rest of the patch is unchanged.
        contentLabelsPatch =
          contentLabels.length > 0 ? { default: contentLabels } : storedContentLabels.length > 0 ? null : undefined;
      }

      setIsSaving(true);
      const body: ContentPatchBodyWithContentLabels = {
        // Content types without the section must not send the field at all, the API rejects it
        ...(hasReverseChronological && { reverseOrder }),
        ...(synonymsChanged && { synonyms: data.synonyms }),
        ...(contentLabelsPatch !== undefined && { contentLabels: contentLabelsPatch }),
        contentGroups,
        lastSearch,
        ...(data.redirection?.value && {
          extendedMetadata: {
            redirection: {
              type: data.redirection.type,
              value: data.redirection.value
            }
          }
        })
      };
      // Generated ContentPatchBody names this key `_default`.
      // The request keeps `default`, which is what the API stores.
      const content = await patchContent(contentId, body as ContentPatchBody);

      if (content) {
        clearPersistedSearchParams();
        closeForm();
        return;
      }
      setIsSaving(false);
    }
  };

  const handleSaveAndPublish = async () => {
    methods.handleSubmit(handleSubmit)();
  };

  return (
    <Box className={classes.root} data-testid={enrichTestIds.formBody}>
      <FormProvider {...methods} handleSubmit={methods.handleSubmit}>
        {/* The Lokalise field builds on TextValidator, which attaches itself to a ValidatorForm on
            mount. The content drawer renders no form of its own, so the sections get one here.
            Submitting still goes through react-hook-form from the Update button. */}
        <ValidatorForm onSubmit={handleSaveAndPublish} instantValidate={false}>
          <Box className={classes.formContainer}>
            {hasReverseChronological && <ReverseChronological control={control} isSaving={isSaving} />}
            {hasSynonyms && <SearchSynonyms control={control} isSaving={isSaving} />}
            <ContentEntitlementFilteringField control={control} isSaving={isSaving} />
            <DeepLink control={control} isSaving={isSaving} />
            {isContentLabelsActive && <ContentLabels control={control} isSaving={isSaving} />}
          </Box>
          <Box className={classes.saveAndPublishBtn}>
            <Button type="button" onClick={handleSaveAndPublish} disabled={!canPublishReverseChron || isSaving}>
              {t('general.update')}
            </Button>
          </Box>
        </ValidatorForm>
      </FormProvider>
    </Box>
  );
}
